import React, { useEffect, useState } from 'react';
import { team, founders, investmentTeam } from '../data/mock';
import { useContent, useTeam, rich } from '../hooks/useContent';
import { resolveAssetUrl } from '../lib/api';
import { Mail, Phone, X, User } from 'lucide-react';

const Team = () => {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  const [openMember, setOpenMember] = useState(null);
  const { t } = useContent('team');
  const cmsMembers = useTeam();

  const DEFAULT_HERO = 'https://customer-assets-rejwkqb3.emergentagent.net/job_stone-temple-preview/artifacts/889jifuf_DSC00150.webp';
  const heroPhoto = resolveAssetUrl(t('hero.photo_url', '') || DEFAULT_HERO);

  // Normalize CMS members (photo URLs may be /api/files/... paths) and merge with defaults
  const mapCms = (m) => ({ ...m, photo: resolveAssetUrl(m.photo_url) });
  const cmsFounders = (cmsMembers || []).filter((m) => m.group === 'founder').map(mapCms);
  const cmsInvestment = (cmsMembers || []).filter((m) => m.group === 'investment').map(mapCms);
  const cmsAdvisory = (cmsMembers || []).filter((m) => m.group === 'advisory').map(mapCms);
  // Prefer CMS entries when present; otherwise fall back to mock defaults
  const foundersList = cmsFounders.length ? cmsFounders : founders;
  const investmentList = cmsInvestment.length ? cmsInvestment : investmentTeam;
  const advisoryList = cmsAdvisory.length ? cmsAdvisory : team;

  return (
    <main className="pt-32 pb-24" data-testid="team-page">
      {/* Hero — intro left, photo right (left-aligned) */}
      <section className="max-w-[1440px] mx-auto px-8 md:px-14 py-12 md:py-16" data-testid="team-hero">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7 text-left">
            <p className="font-sans text-[11px] tracking-[0.35em] text-navy/60 mb-8">{t('hero.kicker', 'OUR FIRM')}</p>
            <h1 className="font-serif text-6xl md:text-8xl text-navy leading-[0.95]" data-testid="team-heading">
              {t('hero.title', 'Team.')}
            </h1>
            <p className="font-serif text-2xl md:text-3xl text-navy leading-snug mt-10 max-w-3xl">
              {t('hero.subtitle', 'Built by practitioners, backed by our own capital.')}
            </p>
            <p className="font-serif text-xl text-navy/75 leading-snug mt-6 max-w-3xl">
              {t('hero.body', "Stone Temple Partners was founded by a team that has spent nearly a decade running dozens of sophisticated family offices — including our own. We don't bring a solution to clients until we understand their near-term objectives, long-term goals, and their values.")}
            </p>
          </div>
          <div className="lg:col-span-5 flex lg:justify-end">
            <div className="relative w-full max-w-md">
              <div className="absolute -inset-3 border border-navy/20" />
              <div className="relative overflow-hidden bg-cream2">
                <img src={heroPhoto} alt="Stone Temple Partners" className="block w-full h-auto" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Founders — 3 columns, bio fits on smaller laptops (compact) */}
      <section className="py-10 md:py-14 border-t border-navy/10" data-testid="team-founders">
        <div className="max-w-[1440px] mx-auto px-8 md:px-14">
          <p className="font-serif italic text-2xl text-navy/60 mb-8">{t('founders.label')}</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-start">
            {foundersList.map((f) => (
              <FounderCard key={f.slug} founder={f} viewBio={t('founders.view_bio')} onOpen={() => setOpenMember(f)} />
            ))}
          </div>
        </div>
      </section>

      {/* Team — floating heads (name + role only, bio on hover/click) */}
      <section className="py-14 md:py-20 border-t border-navy/10" data-testid="team-grid-section">
        <div className="max-w-[1440px] mx-auto px-8 md:px-14">
          <h3 className="font-serif text-4xl md:text-5xl text-navy leading-tight max-w-3xl">
            {rich(t('grid.title'))}
          </h3>

          <div className="mt-14 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-8 gap-y-14" data-testid="team-floating-heads">
            {[...investmentList, ...advisoryList].map((m) => (
              <FloatingHead key={m.slug} member={m} hoverText={t('grid.hover')} onOpen={() => setOpenMember(m)} />
            ))}
          </div>
        </div>
      </section>

      {/* What earns us the right — moved to bottom, before footer */}
      <section className="py-16 md:py-24 border-t border-navy/10" data-testid="team-earns-right">
        <div className="max-w-[1440px] mx-auto px-8 md:px-14">
          <p className="font-sans text-[11px] tracking-[0.35em] text-navy/60 mb-6">{t('earns.kicker')}</p>
          <h2 className="font-serif text-4xl md:text-6xl text-navy leading-tight max-w-4xl">
            {rich(t('earns.title'))}
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-14">
            {(t('earns.items') || []).map((p, i) => (
              <div key={i} className="border-t border-navy/25 pt-6">
                <h3 className="font-serif text-2xl text-navy leading-tight">{p.title}</h3>
                <p className="font-sans text-sm text-navy/75 mt-4 leading-relaxed">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {openMember && <MemberModal member={openMember} t={t} onClose={() => setOpenMember(null)} />}
    </main>
  );
};

/* Founder card — per-slug object-position to normalize face alignment across different natural crops */
const FOUNDER_PHOTO_FOCUS = {
  'tim-hall': '50% 0%',
  'alex-herman': '50% 20%',
  'david-ward': '50% 20%',
};

const FounderCard = ({ founder, viewBio, onOpen }) => (
  <button
    onClick={onOpen}
    className="group text-left w-full"
    data-testid={`founder-card-${founder.slug}`}
  >
    <div className="relative overflow-hidden bg-cream2">
      <div className="aspect-[4/5] overflow-hidden">
        <img
          src={founder.photo}
          alt={founder.name}
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
          style={{ objectPosition: FOUNDER_PHOTO_FOCUS[founder.slug] || '50% 20%' }}
        />
      </div>
    </div>
    <div className="pt-5">
      <h3 className="font-serif text-2xl text-navy leading-tight">{founder.name}</h3>
      <p className="font-serif italic text-sm mt-1 text-navy/60">{founder.title}{founder.tenure ? ` · ${founder.tenure}` : ''}</p>
      <p className="font-sans text-sm text-navy/80 mt-4 leading-relaxed line-clamp-3">{founder.summary}</p>
      <p className="font-sans text-[10px] tracking-[0.3em] text-navy/60 mt-4 flex items-center gap-2 group-hover:text-navy transition-colors">
        {viewBio} <span className="inline-block transition-transform group-hover:translate-x-1">→</span>
      </p>
    </div>
  </button>
);

/* Floating head — name + role by default; bio reveals on hover (desktop) / tap (mobile) */
const FloatingHead = ({ member, hoverText, onOpen }) => {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocus={() => setHovered(true)}
      onBlur={() => setHovered(false)}
      onClick={onOpen}
      className="group text-left flex flex-col items-center"
      data-testid={`floating-head-${member.slug}`}
    >
      <div className="relative w-32 h-32 md:w-40 md:h-40 rounded-full overflow-hidden bg-cream2 shadow-sm ring-1 ring-navy/10 transition-transform duration-500 group-hover:-translate-y-1 group-hover:shadow-lg">
        {member.photo ? (
          <img
            src={member.photo}
            alt={member.name}
            className="w-full h-full object-cover object-top"
          />
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-cream2 to-stone/60">
            <User className="w-8 h-8 text-navy/40" strokeWidth={1.5} />
            <span className="font-serif text-lg text-navy/50 mt-1">
              {member.name.split(' ').map(w => w[0]).join('').slice(0, 2)}
            </span>
          </div>
        )}
        {/* Reveal overlay on hover */}
        <div
          className={`absolute inset-0 bg-navy/85 backdrop-blur-[2px] p-3 flex items-center justify-center text-center transition-opacity duration-300 ${hovered ? 'opacity-100' : 'opacity-0'}`}
        >
          <p className="font-sans text-[10px] tracking-[0.25em] text-cream leading-relaxed">
            {hoverText}
          </p>
        </div>
      </div>
      <h4 className="font-serif text-lg md:text-xl text-navy mt-5 leading-tight text-center">{member.name}</h4>
      <p className="font-serif italic text-xs md:text-sm text-navy/60 mt-1 text-center max-w-[14rem]">{member.title}</p>
    </button>
  );
};

/* Member modal (unchanged, cream palette) */
const MemberModal = ({ member, t, onClose }) => {
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    const onEsc = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onEsc);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onEsc);
    };
  }, [onClose]);

  const group = member.group || (member.title?.startsWith('Founder') ? 'founder' : (member.tenure ? 'advisory' : 'investment'));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 md:p-8" onClick={onClose} data-testid="member-modal">
      <div className="absolute inset-0 bg-navy/60 backdrop-blur-sm" />
      <div
        className="relative bg-cream max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-navy text-cream flex items-center justify-center hover:bg-navydark transition-colors"
          aria-label="Close"
          data-testid="member-modal-close"
        >
          <X className="w-5 h-5" />
        </button>
        <div className="grid grid-cols-1 md:grid-cols-5">
          <div className="md:col-span-2 aspect-square md:aspect-auto md:h-full bg-cream2">
            {member.photo ? (
              <img src={member.photo} alt={member.name} className="w-full h-full object-cover object-top" />
            ) : (
              <div className="w-full h-full flex flex-col items-center justify-center min-h-[300px] bg-gradient-to-br from-cream2 to-stone/60">
                <User className="w-16 h-16 text-navy/30 mb-4" strokeWidth={1.5} />
                <span className="font-serif text-4xl text-navy/50">{member.name.split(' ').map(w => w[0]).join('').slice(0, 2)}</span>
                <span className="font-sans text-[10px] tracking-[0.3em] text-navy/40 mt-3">PHOTO COMING SOON</span>
              </div>
            )}
          </div>
          <div className="md:col-span-3 p-8 md:p-12">
            <p className="font-sans text-[10px] tracking-[0.3em] text-navy/60 mb-4">{t(`modal.${group}`)}</p>
            <h3 className="font-serif text-4xl md:text-5xl text-navy leading-tight">{member.name}</h3>
            <p className="font-serif italic text-lg text-navy/70 mt-2">
              {member.title}{member.tenure ? ` · ${member.tenure}` : ''}
            </p>

            {member.detail?.length > 0 && (
              <div className="mt-8 pt-8 border-t border-navy/15">
                <p className="font-sans text-[10px] tracking-[0.3em] text-navy/60 mb-4">{t('modal.experience')}</p>
                <ul className="space-y-4">
                  {member.detail.map((line, i) => (
                    <li key={i} className="flex gap-3 font-serif text-base text-navy/85 leading-snug">
                      <span className="text-navy/40 mt-1.5 w-1.5 h-1.5 rounded-full bg-navy/30 flex-shrink-0" />
                      <span>{line}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {(member.email || member.phone) && (
              <div className="mt-8 pt-6 border-t border-navy/15 flex flex-wrap gap-6">
                {member.email && (
                  <a href={`mailto:${member.email}`} className="flex items-center gap-2 font-sans text-sm text-navy hover:opacity-70">
                    <Mail className="w-4 h-4" strokeWidth={1.5} />
                    {member.email}
                  </a>
                )}
                {member.phone && (
                  <span className="flex items-center gap-2 font-sans text-sm text-navy/70">
                    <Phone className="w-4 h-4" strokeWidth={1.5} />
                    {member.phone}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Team;
